import { formatDate, formatTime } from "@/lib/utils";

export interface SessionData {
  id: string;
  sessionType?: string;
  callType?: string;
  status?: string;
  duration?: number;
  createdAt: string;
  updatedAt: string;
  startedAt?: string | null;
  endedAt?: string | null;
  studentId?: string;
  counselorId?: string;
  student?: {
    id?: string;
    name?: string | null;
    email?: string | null;
    phoneNumber?: string;
    grade?: string | null;
    schoolName?: string | null;
    profilePicture?: string | null;
    interests?: string[];
    bio?: string | null;
  };
  messages?: any[];
  rating?: number | null;
  feedback?: string | null;
  recordingUrl?: string | null;
  provider?: string;
  meetingId?: string;
}

export const generateCSV = (sessions: SessionData[]): string => {
  // Define CSV headers
  const headers = [
    'Session ID',
    'Session Type',
    'Call Type',
    'Status',
    'Duration (minutes)',
    'Created Date',
    'Created Time',
    'Started Date',
    'Started Time',
    'Ended Date',
    'Ended Time',
    'Student Name',
    'Student Email',
    'Student Phone',
    'Grade',
    'School Name',
    'Interests',
    'Student Bio',
    'Message Count',
    'Rating',
    'Feedback',
    'Recording URL',
    'Provider',
    'Meeting ID'
  ];

  // Helper function to escape CSV fields
  const escapeCSVField = (field: any): string => {
    if (field === null || field === undefined) return '';
    const str = String(field);
    // Escape quotes and wrap in quotes if contains comma, quote, or newline
    if (str.includes('"') || str.includes(',') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Helper function to format date for CSV
  const formatDateForCSV = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    try {
      return formatDate(dateString);
    } catch {
      return '';
    }
  };

  // Helper function to format time for CSV
  const formatTimeForCSV = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    try {
      return formatTime(dateString);
    } catch {
      return '';
    }
  };

  // Generate CSV rows
  const rows = sessions.map(session => [
    escapeCSVField(session.id),
    escapeCSVField(session.sessionType || ''),
    escapeCSVField(session.callType || ''),
    escapeCSVField(session.status || ''),
    escapeCSVField(session.duration || 0),
    escapeCSVField(formatDateForCSV(session.createdAt)),
    escapeCSVField(formatTimeForCSV(session.createdAt)),
    escapeCSVField(formatDateForCSV(session.startedAt)),
    escapeCSVField(formatTimeForCSV(session.startedAt)),
    escapeCSVField(formatDateForCSV(session.endedAt)),
    escapeCSVField(formatTimeForCSV(session.endedAt)),
    escapeCSVField(session.student?.name || ''),
    escapeCSVField(session.student?.email || ''),
    escapeCSVField(session.student?.phoneNumber || ''),
    escapeCSVField(session.student?.grade || ''),
    escapeCSVField(session.student?.schoolName || ''),
    escapeCSVField(session.student?.interests?.join(', ') || ''),
    escapeCSVField(session.student?.bio || ''),
    escapeCSVField(session.messages?.length || 0),
    escapeCSVField(session.rating || ''),
    escapeCSVField(session.feedback || ''),
    escapeCSVField(session.recordingUrl || ''),
    escapeCSVField(session.provider || ''),
    escapeCSVField(session.meetingId || '')
  ]);

  // Combine headers and rows
  const csvContent = [headers, ...rows]
    .map(row => row.join(','))
    .join('\n');

  return csvContent;
};

export const downloadCSV = (csvContent: string, filename?: string): void => {
  // Create filename with current date if not provided
  const currentDate = new Date().toISOString().split('T')[0];
  const finalFilename = filename || `sessions_export_${currentDate}.csv`;

  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', finalFilename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } else {
    // Fallback for older browsers
    window.open('data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent));
  }
};

export const exportSessionsToCSV = (sessions: SessionData[], filename?: string): void => {
  try {
    const csvContent = generateCSV(sessions);
    downloadCSV(csvContent, filename);
  } catch (error) {
    console.error('Error exporting sessions to CSV:', error);
    throw new Error('Failed to export sessions to CSV');
  }
};